import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Pencil, Search, Trash2 } from 'lucide-react';
import IpeToolbarSelect from '@/pages/components/IpeToolbarSelect';
import {
  DEFAULT_TITLE_PRESET_SCOPE,
  resolveLockedTitlePresetScope,
  resolveVisibleTitlePresetScopes,
} from '@/pages/ipe/workbench/constants/titlePresetCatalog';
import {
  buildVideoI2vCaseOwnerFilterOptions,
  loadVideoI2vPromptCasesPage,
  resolveVideoI2vCaseOwnerLabel,
  VIDEO_I2V_CASE_DEFAULT_PAGE_SIZE,
  VIDEO_I2V_CASE_PAGE_SIZE_OPTIONS,
} from '@/pages/ipe/workbench/utils/videoI2vPromptCaseLoader';
import LibraryModalPagination from '@/pages/ipe/workbench/modals/LibraryModalPagination';
import SignedImage from '@/components/ipe/SignedImage';
import useStudioSidebarDockLeft from '@/pages/ipe/workbench/hooks/useStudioSidebarDockLeft';
import { useHistoryPanelTransition } from '@/pages/ipe/workbench/hooks/useHistoryPanelTransition';
import { useIpeWorkspaceMode } from '@/pages/ipe/workbench/hooks/useIpeWorkspaceMode';
import '@/styles/ipe/workbench/modals/Text2ImgPromptCaseLibraryModal.css';

function CaseCard({
  item,
  active,
  onSelect,
  onEdit,
  onDelete,
  currentUserId,
}) {
  const { t } = useTranslation('workbench');
  const scopeLabel = item.scope === 'personal'
    ? t('promptCaseLib.scopePersonal')
    : item.scope === 'team'
      ? t('promptCaseLib.scopeTeam')
      : t('promptCaseLib.scopeOfficial');
  const ownerLabel = item.scope === 'personal'
    ? ''
    : resolveVideoI2vCaseOwnerLabel(item, { currentUserId });
  const showEdit = Boolean(item.canEdit && onEdit);
  const showDelete = Boolean(item.canDelete && onDelete);
  const showActions = showEdit || showDelete;
  const actionCount = Number(showEdit) + Number(showDelete);
  const hasMeta = Boolean(ownerLabel);
  const coverUrl = String(item.coverUrl || '').trim();

  return (
    <div className={`t2i-case-library__card-wrap${active ? ' is-active' : ''}`}>
      <button
        type="button"
        className={`t2i-case-library__card${active ? ' is-active' : ''}`}
        onClick={() => onSelect?.(item)}
        title={item.prompt}
      >
        {coverUrl ? (
          <div className="t2i-case-library__card-cover">
            <SignedImage
              url={coverUrl}
              alt={item.name}
              loading="lazy"
              variant="thumb"
              className="t2i-case-library__card-cover-image"
            />
          </div>
        ) : null}
        <div className="t2i-case-library__card-head">
          <span className="t2i-case-library__card-title">{item.name}</span>
          <span className={`t2i-case-library__badge t2i-case-library__badge--${item.scope || 'official'}`}>
            {scopeLabel}
          </span>
        </div>
        <p className="t2i-case-library__card-desc">{item.prompt}</p>
        <div
          className={`t2i-case-library__card-meta${showActions ? ` t2i-case-library__card-meta--with-actions-${actionCount}` : ''}`}
        >
          {item.generationMode ? (
            <span className="t2i-case-library__chip">{item.generationMode}</span>
          ) : null}
          {item.aspectRatio ? (
            <span className="t2i-case-library__chip">{item.aspectRatio}</span>
          ) : null}
          {item.durationSec != null ? (
            <span className="t2i-case-library__chip">{item.durationSec}s</span>
          ) : null}
          {item.seed != null && item.seed !== '' ? (
            <span className="t2i-case-library__chip">{t('promptCaseLib.seedChip', { seed: item.seed })}</span>
          ) : null}
          {hasMeta ? (
            <span className="t2i-case-library__chip t2i-case-library__chip--owner">
              {ownerLabel}
            </span>
          ) : null}
        </div>
      </button>
      {showActions ? (
        <div className="t2i-case-library__card-actions">
          {showEdit ? (
            <button
              type="button"
              className="t2i-case-library__card-action"
              title={t('promptCaseLib.editTitle')}
              aria-label={t('promptCaseLib.editAria', { name: item.name })}
              onClick={(e) => {
                e.stopPropagation();
                onEdit?.(item);
              }}
            >
              <Pencil size={14} aria-hidden="true" />
            </button>
          ) : null}
          {showDelete ? (
            <button
              type="button"
              className="t2i-case-library__card-action t2i-case-library__card-action--danger"
              title={t('promptCaseLib.deleteTitle')}
              aria-label={t('promptCaseLib.deleteAria', { name: item.name })}
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.(item);
              }}
            >
              <Trash2 size={14} aria-hidden="true" />
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * 图生视频模板库：个人 / 团队 / 官方 Tab（按顶栏工作区模式决定可见范围），服务端真分页。
 * 默认选中顶栏模式对应的 Tab；各 Tab 只展示自己的模板，不与官方混排。
 */
export default function VideoI2vPromptCaseLibraryModal({
  isOpen,
  onClose,
  selectedId = '',
  /** 可选：外部指定的初始 Tab；未指定时跟随顶栏工作区模式 */
  initialScope = '',
  /** 可选：工作台顶栏模式（不传则自动跟随顶栏） */
  workspaceMode,
  canAccessTeamPresets = false,
  currentUserId = '',
  ownerNameByUserId = null,
  onSelect,
  onRequestCreate,
  onRequestEdit,
  onRequestDelete,
  escapeDisabled = false,
  refreshNonce = 0,
  /** 居中弹窗：工作流节点参数面板内使用（功能页默认仍贴左侧参数栏滑入） */
  centered = false,
}) {
  const {
    mounted,
    exiting,
    requestClose,
    handlePanelAnimationEnd,
  } = useHistoryPanelTransition(isOpen, onClose, {
    baseClassName: 't2i-case-library-modal-layer',
    deferContent: false,
    closeNotifiesParent: true,
  });
  const dockLeft = useStudioSidebarDockLeft(mounted && !centered);
  const { t } = useTranslation('workbench');
  const workspaceModeFromHook = useIpeWorkspaceMode();
  const activeWorkspaceMode = workspaceMode || workspaceModeFromHook;
  const visibleScopes = useMemo(
    () => resolveVisibleTitlePresetScopes(activeWorkspaceMode, canAccessTeamPresets),
    [activeWorkspaceMode, canAccessTeamPresets],
  );
  const preferredScope = useMemo(() => {
    // 默认 Tab 跟随顶栏工作区模式；外部显式指定的 scope 仅在模式不可用时兜底
    const byMode = resolveLockedTitlePresetScope(activeWorkspaceMode, canAccessTeamPresets);
    if (visibleScopes.some((item) => item.key === byMode)) return byMode;
    if (initialScope && visibleScopes.some((item) => item.key === initialScope)) {
      return initialScope;
    }
    return visibleScopes[0]?.key || DEFAULT_TITLE_PRESET_SCOPE;
  }, [initialScope, activeWorkspaceMode, canAccessTeamPresets, visibleScopes]);
  const [scope, setScope] = useState(preferredScope);
  const ownerFilterOptions = useMemo(
    () => buildVideoI2vCaseOwnerFilterOptions({ ownerNameByUserId, currentUserId }),
    [ownerNameByUserId, currentUserId],
  );
  const [keyword, setKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');
  const [ownerUserId, setOwnerUserId] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(VIDEO_I2V_CASE_DEFAULT_PAGE_SIZE);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const requestSeqRef = useRef(0);

  useEffect(() => {
    if (!isOpen) return;
    setScope(preferredScope);
    setKeyword('');
    setDebouncedKeyword('');
    setOwnerUserId('');
    setPage(1);
    setPageSize(VIDEO_I2V_CASE_DEFAULT_PAGE_SIZE);
  }, [isOpen, preferredScope]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const timer = window.setTimeout(() => {
      setDebouncedKeyword(String(keyword || '').trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [keyword, isOpen]);

  useEffect(() => {
    if (!isOpen || escapeDisabled) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, requestClose, escapeDisabled]);

  useEffect(() => {
    if (!isOpen) return undefined;

    if (scope === 'team' && !canAccessTeamPresets) {
      setItems([]);
      setTotal(0);
      setLoadError('');
      setLoading(false);
      return undefined;
    }

    const seq = ++requestSeqRef.current;
    let cancelled = false;
    setLoading(true);
    setLoadError('');

    loadVideoI2vPromptCasesPage({
      scope,
      keyword: debouncedKeyword,
      ownerUserId: scope === 'team' ? ownerUserId : '',
      page,
      pageSize,
    })
      .then((res) => {
        if (cancelled || seq !== requestSeqRef.current) return;
        const nextTotal = Number(res.total) || 0;
        const maxPage = Math.max(1, Math.ceil(nextTotal / pageSize) || 1);
        if (page > maxPage) {
          setPage(maxPage);
          return;
        }
        setItems(res.items || []);
        setTotal(nextTotal);
      })
      .catch((err) => {
        if (cancelled || seq !== requestSeqRef.current) return;
        setItems([]);
        setTotal(0);
        setLoadError(err?.message || t('promptCaseLib.loadFailed'));
      })
      .finally(() => {
        if (cancelled || seq !== requestSeqRef.current) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    scope,
    debouncedKeyword,
    ownerUserId,
    page,
    pageSize,
    canAccessTeamPresets,
    refreshNonce,
    t,
  ]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const canCreateInScope = scope === 'personal' || scope === 'team';
  const showOwnerFilter = scope === 'team' && canAccessTeamPresets;

  if (!mounted) return null;

  const handleSelect = (item) => {
    onSelect?.(item);
    requestClose();
  };

  const handleScopeChange = (nextScope) => {
    if (nextScope === scope) return;
    setScope(nextScope);
    setOwnerUserId('');
    setPage(1);
  };

  const handleOwnerFilterChange = (nextOwnerId) => {
    setOwnerUserId(nextOwnerId == null ? '' : String(nextOwnerId));
    setPage(1);
  };

  const handlePageSizeChange = (nextSize) => {
    setPageSize(nextSize);
    setPage(1);
  };

  const emptyText = (() => {
    if (scope === 'team' && !canAccessTeamPresets) return t('promptCaseLib.teamLocked');
    if (loadError) return loadError;
    if (loading) return t('promptCaseLib.loading');
    if (debouncedKeyword || (showOwnerFilter && ownerUserId)) return t('promptCaseLib.emptyNoMatch');
    return t('promptCaseLib.emptyNone');
  })();

  const modal = (
    <div
      className={`funding-modal funding-modal--top t2i-case-library-modal-layer${centered ? ' ipe-case-modal--centered' : ''}${exiting ? ' is-closing' : ' is-opening'}`}
      style={{ '--t2i-case-library-dock-left': `${dockLeft}px` }}
    >
      <div
        className="funding-modal__backdrop"
        role="presentation"
        onClick={requestClose}
      />
      <div
        className="funding-modal__panel funding-modal__panel--unified funding-modal__panel--t2i-case-library"
        role="dialog"
        aria-modal="true"
        aria-label={t('promptCaseLib.i2vTitle')}
        onAnimationEnd={handlePanelAnimationEnd}
      >
        <div className="funding-modal__body t2i-case-library__body">
          <div
            className="t2i-case-library__scopes"
            style={{ '--t2i-case-scope-cols': visibleScopes.length }}
            role="tablist"
            aria-label={t('promptCaseLib.scopeAria')}
          >
            {visibleScopes.map((item) => (
              <button
                key={item.key}
                type="button"
                role="tab"
                aria-selected={scope === item.key}
                className={`t2i-case-library__scope${scope === item.key ? ' is-active' : ''}`}
                onClick={() => handleScopeChange(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="t2i-case-library__toolbar">
            {showOwnerFilter ? (
              <IpeToolbarSelect
                id="i2v-case-library-owner"
                className="ipe-owner-filter-select t2i-case-library__owner-filter"
                value={ownerUserId}
                onChange={handleOwnerFilterChange}
                options={ownerFilterOptions}
                ariaLabel={t('promptCaseLib.ownerFilterAria')}
                variant="light"
                clearable={false}
                width={148}
              />
            ) : null}
            <label className="t2i-case-library__search">
              <Search size={16} className="t2i-case-library__search-icon" aria-hidden="true" />
              <input
                type="text"
                className="t2i-case-library__search-input"
                placeholder={t('promptCaseLib.i2vSearchPh')}
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
            </label>
            {canCreateInScope ? (
              <button
                type="button"
                className="funding-button funding-button--accent t2i-case-library__create"
                onClick={() => onRequestCreate?.(scope)}
              >
                {t('promptCaseLib.create')}
              </button>
            ) : null}
          </div>

          <div className="t2i-case-library__content">
            {items.length === 0 ? (
              <div className="t2i-case-library__empty" role="status">
                {emptyText}
              </div>
            ) : (
              <div className={`t2i-case-library__grid${loading ? ' is-loading' : ''}`}>
                {items.map((item) => (
                  <CaseCard
                    key={`${item.scope}-${item.id}`}
                    item={item}
                    active={String(selectedId) === String(item.id)}
                    onSelect={handleSelect}
                    onEdit={onRequestEdit}
                    onDelete={onRequestDelete}
                    currentUserId={currentUserId}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="t2i-case-library__footer">
            <LibraryModalPagination
              currentPage={page}
              totalPages={totalPages}
              totalItems={total}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={VIDEO_I2V_CASE_PAGE_SIZE_OPTIONS}
              pageSizeUnitLabel={t('promptCaseLib.pageUnit')}
              showTotalInfo={false}
              className="t2i-case-library__pagination"
              ariaLabel={t('promptCaseLib.pageAria', { name: t('promptCaseLib.i2vTitle') })}
              showWhenEmpty
            />
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
